import { CrudViewColumnType } from '../../../../../utils';
import { lowerCase } from 'lodash';
import * as moment from 'moment-timezone';

/* if you're changing the values of the other conditions which are commons, please also change here */
export enum CommonCondition {
  IsNotEmpty = 'is-not-empty',
  IsEmpty = 'is-empty',
  IsEqualTo = 'is-equal-to',
  IsNotEqualTo = 'is-not-equal-to'
}

export enum NumberCondition {
  IsEqualTo = 'is-equal-to',
  IsNotEqualTo = 'is-not-equal-to',
  LessThan = 'less-than',
  GreaterThan = 'greater-than',
  IsEmpty = 'is-empty',
  IsNotEmpty = 'is-not-empty'
}

export function numberCondition<T extends Record<string, any>>(
  data: T[] | null | undefined,
  filteredValue: number | string,
  fieldName: keyof T & string,
  condition: NumberCondition,
): T[] {
  if (!Array.isArray(data) || !fieldName) {
    return [];
  }

  const toNum = (v: any): number => {
    if (v === null || v === undefined || v === '' || v === 'N/A' || v === 'NaN' || v === '#N/A') {
      return NaN;
    }
    // allow strings with commas/spaces; otherwise fall back to Number()
    const s = typeof v === 'string' ? v.replace(/[, ]+/g, '') : v;
    const n = typeof s === 'number' ? s : Number(s);
    return Number.isFinite(n) ? n : NaN;
  };

  const fv = toNum(filteredValue);
  const hasFiniteFV = Number.isFinite(fv);

  switch (condition) {
    case NumberCondition.IsEqualTo:
      if (!hasFiniteFV) {
        return [];
      }
      return data.filter(item => {
        const n = toNum(item?.[fieldName]);
        return Number.isFinite(n) && n === fv;
      });

    case NumberCondition.IsNotEqualTo:
      if (!hasFiniteFV) {
        return [];
      }
      return data.filter(item => {
        const n = toNum(item?.[fieldName]);
        return Number.isFinite(n) && n !== fv;
      });

    case NumberCondition.LessThan:
      if (!hasFiniteFV) {
        return [];
      }
      return data.filter(item => {
        const n = toNum(item?.[fieldName]);
        return Number.isFinite(n) && n < fv;
      });

    case NumberCondition.GreaterThan:
      if (!hasFiniteFV) {
        return [];
      }
      return data.filter(item => {
        const n = toNum(item?.[fieldName]);
        return Number.isFinite(n) && n > fv;
      });

    case NumberCondition.IsEmpty:
      // empty if null/undefined/'' OR cannot be parsed to a finite number
      return data.filter(item => {
        const raw = item?.[fieldName];
        if (raw === null || raw === undefined || raw === '') {
          return true;
        }
        return !Number.isFinite(toNum(raw));
      });

    case NumberCondition.IsNotEmpty:
      // not empty if it parses to a finite number (0 is valid)
      return data.filter(item => Number.isFinite(toNum(item?.[fieldName])));

    default:
      return data;
  }
}

export enum StringCondition {
  Contains = 'contains',
  IsEqualTo = 'is-equal-to',
  EndsWith = 'ends-with',
  StartsWith = 'starts-with',
  IsEmpty = 'is-empty',
  IsNotEmpty = 'is-not-empty'
}

export function stringCondition(data: any[], filteredValue: string, fieldName: string, condition: StringCondition) {
  /* replace(/\s/g, '') function removes white spaces */
  if (condition === StringCondition.IsEqualTo) {
    return data.filter(item => lowerCase(item[fieldName]).replace(/\s/g, '') === lowerCase(filteredValue).replace(/\s/g, ''));
  } else if (condition === StringCondition.Contains) {
    return data.filter(item => lowerCase(item[fieldName]).replace(/\s/g, '').includes(lowerCase(filteredValue).replace(/\s/g, '')));
  } else if (condition === StringCondition.EndsWith) {
    return data.filter(item => lowerCase(item[fieldName]).replace(/\s/g, '').endsWith(lowerCase(filteredValue).replace(/\s/g, '')));
  } else if (condition === StringCondition.StartsWith) {
    return data.filter(item => lowerCase(item[fieldName]).replace(/\s/g, '').startsWith(lowerCase(filteredValue).replace(/\s/g, '')));
  } else if (condition === StringCondition.IsEmpty) {
    return data.filter(item => !item[fieldName]);
  } else if (condition === StringCondition.IsNotEmpty) {
    return data.filter(item => item[fieldName]);
  } else {
    return data;
  }
}

export enum DateCondition {
  IsEqualTo = 'is-equal-to',
  IsNotEqualTo = 'is-not-equal-to',
  IsAfterOrEqualTo = 'is-after-or-equal-to',
  IsAfter = 'is-after',
  IsBeforeOrEqualTo = 'is-before-or-equal-to',
  IsBefore = 'is-before',
  IsEmpty = 'is-empty',
  IsNotEmpty = 'is-not-empty'
}


export function dateCondition(data: any[], filteredValue: Date, fieldName: string, condition: DateCondition) {
  if (condition === DateCondition.IsEqualTo) {
    return data.filter(item => moment(moment(item[fieldName]).format('LL')).isSame(moment(moment(filteredValue).format('LL'))));
  } else if (condition === DateCondition.IsAfter) {
    return data.filter(item => moment(moment(item[fieldName]).format('LL')).isAfter(moment(moment(filteredValue).format('LL'))));
  } else if (condition === DateCondition.IsAfterOrEqualTo) {
    return data.filter(item => moment(moment(item[fieldName]).format('LL')).isSameOrAfter(moment(moment(filteredValue).format('LL'))));
  } else if (condition === DateCondition.IsBefore) {
    return data.filter(item => moment(moment(item[fieldName]).format('LL')).isBefore(moment(moment(filteredValue).format('LL'))));
  } else if (condition === DateCondition.IsBeforeOrEqualTo) {
    return data.filter(item => moment(moment(item[fieldName]).format('LL')).isSameOrBefore(moment(moment(filteredValue).format('LL'))));
  } else if (condition === DateCondition.IsNotEqualTo) {
    return data.filter(item => !moment(moment(item[fieldName]).format('LL')).isSame(moment(moment(filteredValue).format('LL'))));
  } else if (condition === DateCondition.IsEmpty) {
    return data.filter(item => !item[fieldName]);
  } else if (condition === DateCondition.IsNotEmpty) {
    return data.filter(item => item[fieldName]);
  } else {
    return data;
  }
}

export enum BooleanCondition {
  IsEmpty = 'is-empty',
  IsNotEmpty = 'is-not-empty'
}

export function booleanCondition(data: any[], filteredValue: boolean, fieldName: string, condition?: BooleanCondition) {
  if (condition) {
    if (condition === BooleanCondition.IsEmpty) {
      return data.filter(item => item[fieldName] == null);
    } else if (condition === BooleanCondition.IsNotEmpty) {
      return data.filter(item => item[fieldName] != null);
    }
  } else {
    return data.filter(item => item[fieldName] === filteredValue);
  }
}

export const conditionMapping: any = [
  {
    type: CrudViewColumnType.Number,
    condition: NumberCondition,
    filterFunction: numberCondition
  },
  {
    type: CrudViewColumnType.Text,
    condition: StringCondition,
    filterFunction: stringCondition
  },
  {
    type: CrudViewColumnType.Link,
    condition: StringCondition,
    filterFunction: stringCondition
  },
  {
    type: CrudViewColumnType.Boolean,
    condition: BooleanCondition,
    filterFunction: booleanCondition
  },
  {
    type: CrudViewColumnType.Date,
    condition: DateCondition,
    filterFunction: dateCondition
  },
  {
    type: CrudViewColumnType.DateTime,
    condition: DateCondition,
    filterFunction: dateCondition
  },
  {
    type: CrudViewColumnType.Tag,
    condition: StringCondition,
    filterFunction: stringCondition
  },
  {
    type: CrudViewColumnType.TagList,
    condition: StringCondition,
    filterFunction: stringCondition
  },
  {
    type: CrudViewColumnType.Icon,
    condition: StringCondition,
    filterFunction: stringCondition
  },
  {
    type: CrudViewColumnType.Image,
    condition: StringCondition,
    filterFunction: stringCondition
  },
  {
    type: CrudViewColumnType.Currency,
    condition: NumberCondition,
    filterFunction: numberCondition
  },
  {
    type: CrudViewColumnType.Rate,
    condition: NumberCondition,
    filterFunction: numberCondition
  },
  {
    type: CrudViewColumnType.ActionLink,
    condition: StringCondition,
    filterFunction: stringCondition
  },
  {
    type: CrudViewColumnType.Code,
    condition: StringCondition,
    filterFunction: stringCondition
  },
  {
    type: CrudViewColumnType.InputTemplate,
    condition: StringCondition,
    filterFunction: stringCondition
  }
];
